import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox,
    Toolbar,
    alpha,
    FormControlLabel,
    RadioGroup,
    Radio,
    Switch,
    Menu,
    MenuItem,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
    ArrowBack as BackIcon,
    SignalWifiStatusbar4Bar as OnlineIcon,
    SignalWifiOff as OfflineIcon,
    Battery20 as BatteryLowIcon,
    Battery80 as BatteryOkIcon,
    BatteryUnknown as BatteryUnknownIcon,
    Thermostat as TempIcon,
    Edit as EditIcon,
    VolumeUp as SirenaIcon,
    VolumeOff as SirenaOffIcon,
    NotificationsOff as MuteIcon,
    Notifications as UnmuteIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon,
    Settings as SettingsIcon,
    AutoFixHigh as WizardIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    PersonAdd as PersonAddIcon,
    MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
    IotSenzor,
    IotSenzorUdalost,
    UpdateIotSenzorRequest,
    SenzorTyp,
    SenzorUdalostTyp,
    CreateIotPrijemceRequest,
    SenzorCapabilities,
} from '../../api/JsdhNotifikaceService/generated';
import {
    useIotObjektQuery,
    useIotSenzorHistorieQuery,
    useUpdateIotSenzorMutation,
    useMuteIotSenzorMutation,
    useUnmuteIotSenzorMutation,
    useStartSirenaMutation,
    useStopSirenaMutation,
    useMuteBatchSenzoryMutation,
    useMuteObjektMutation,
    useUnmuteObjektMutation,
    useAddIotPrijemceMutation,
    useRemoveIotPrijemceMutation,
    useObjektCapabilitiesQuery,
} from '../../api/JsdhNotifikaceService/service/IotMutations';
import { hasWorkingSiren } from '../../utils/sensorCapabilities';
import TeplotaChart from '../../components/iot/TeplotaChart';
import MqttCredentialsDialog from '../../components/iot/MqttCredentialsDialog';
import OnboardingWizard from '../../components/iot/OnboardingWizard';
import PrijemceFormDialog from '../../components/iot/PrijemceFormDialog';
import EpsEzsSection from '../../components/iot/EpsEzsSection';
import ZarizeniSection from '../../components/iot/ZarizeniSection';
import { usePreteceZalozek } from '../../components/iot/usePreteceZalozek';
import RelayControlSection from '../../components/iot/RelayControlSection';
import DeviceCapabilitiesPanel from '../../components/iot/DeviceCapabilitiesPanel';
import { PageHeader } from '../../components/admin';
import EmptyState from '../../components/admin/EmptyState';
import { useStavyZarizeniQuery, useZarizeniObjektuQuery } from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import { useSiaAccountsByObjektQuery } from '../../api/JsdhNotifikaceService/service/SiaMutations';
import {
    TOOLTIP_STAV_NEZNAMY, jeOvladaneZarizeni, nazevTypuSenzoru, pocetNedostupnychEvidence, rpiNedostupne, rpiNeozvalo, stavSpojeniCidla, stavSpojeniRpi, textNedostupnaCidla, textNedostupnaZarizeni,
} from '../../components/iot/iotStav';

// =====================
// HELPER FUNCTIONS
// =====================

const getSenzorTypLabel = (typ: SenzorTyp): string => nazevTypuSenzoru(typ);

const getSenzorTypColor = (typ: SenzorTyp): 'error' | 'warning' | 'info' | 'primary' => {
    switch (typ) {
        case 'SMOKE': return 'error';
        case 'CO': return 'error';
        case 'TEMPERATURE': return 'info';
        case 'WATER_LEAK': return 'warning';
        default: return 'primary';
    }
};

const getUdalostTypLabel = (typ: SenzorUdalostTyp): string => {
    switch (typ) {
        case 'SMOKE': return 'Kouř detekován';
        case 'HIGH_TEMP': return 'Vysoká teplota';
        case 'CO': return 'CO detekován';
        case 'WATER_LEAK': return 'Zaplavení';
        case 'BATTERY_LOW': return 'Nízká baterie';
        case 'FAULT': return 'Porucha';
        case 'HEARTBEAT': return 'Heartbeat';
        case 'OFFLINE': return 'Nedostupné';
        default: return typ;
    }
};

const getUdalostTypColor = (typ: SenzorUdalostTyp): 'error' | 'warning' | 'info' | 'default' => {
    switch (typ) {
        case 'SMOKE':
        case 'CO':
            return 'error';
        case 'HIGH_TEMP':
        case 'WATER_LEAK':
        case 'FAULT':
            return 'warning';
        case 'BATTERY_LOW':
        case 'OFFLINE':
            return 'info';
        default:
            return 'default';
    }
};

const formatObjektAddress = (objekt: {
    obec?: string | null;
    castObce?: string | null;
    ulice?: string | null;
    cisloPopisne?: string | null;
}): string | null => {
    const parts: string[] = [];

    // Ulice s číslem popisným
    if (objekt.ulice) {
        parts.push(objekt.cisloPopisne ? `${objekt.ulice} ${objekt.cisloPopisne}` : objekt.ulice);
    } else if (objekt.cisloPopisne) {
        parts.push(`č.p. ${objekt.cisloPopisne}`);
    }

    // Obec s částí obce
    if (objekt.obec) {
        parts.push(objekt.castObce ? `${objekt.obec} - ${objekt.castObce}` : objekt.obec);
    }

    return parts.length > 0 ? parts.join(', ') : null;
};

// =====================
// SENZOR CARD COMPONENT
// =====================

interface SenzorCardProps {
    senzor: IotSenzor;
    objektId: number;
    objektNazev: string;
    onEdit: (senzor: IotSenzor) => void;
    selected?: boolean;
    onSelect?: (senzorId: number, selected: boolean) => void;
    selectionMode?: boolean;
    capabilities?: SenzorCapabilities;
}

const LEGACY_SIREN_TYPES = new Set(['SMOKE', 'CO']);

const SenzorCard: React.FC<SenzorCardProps> = ({
    senzor,
    objektId,
    objektNazev,
    onEdit,
    selected = false,
    onSelect,
    selectionMode = false,
    capabilities,
}) => {
    const muteMutation = useMuteIotSenzorMutation();
    const unmuteMutation = useUnmuteIotSenzorMutation();
    const startSirenaMutation = useStartSirenaMutation();
    const stopSirenaMutation = useStopSirenaMutation();

    const [muteDialogOpen, setMuteDialogOpen] = useState(false);
    const [muteMode, setMuteMode] = useState<'minutes' | 'datetime'>('minutes');
    const [muteMinuty, setMuteMinuty] = useState(30);
    const [muteDo, setMuteDo] = useState<Dayjs | null>(dayjs().add(1, 'hour'));
    const [startSirenaConfirmOpen, setStartSirenaConfirmOpen] = useState(false);

    const isMuted = senzor.muteDo && new Date(senzor.muteDo) > new Date();
    // Stav spojení podle sdíleného slovníku (spec 1.1, práh 30 min beze změny).
    const spojeni = stavSpojeniCidla(senzor.posledniKontakt);

    const handleMute = () => {
        // Posíláme lokální čas bez zóny (BE ukládá do LocalDateTime) — toISOString() by
        // převedlo na UTC a posunulo čas o offset (v létě o 2 h).
        const data = muteMode === 'datetime' && muteDo
            ? { muteDo: muteDo.format('YYYY-MM-DDTHH:mm:ss') }
            : { minuty: muteMinuty };

        muteMutation.mutate(
            { id: senzor.id, data },
            { onSuccess: () => setMuteDialogOpen(false) }
        );
    };

    const handleUnmute = () => {
        unmuteMutation.mutate(senzor.id);
    };

    const handleStartSirena = () => {
        startSirenaMutation.mutate(
            { objektId, senzorId: senzor.id },
            { onSuccess: () => setStartSirenaConfirmOpen(false) }
        );
    };

    const handleStopSirena = () => {
        stopSirenaMutation.mutate({ objektId, senzorId: senzor.id });
    };

    const handleCardClick = () => {
        if (selectionMode && onSelect) {
            onSelect(senzor.id, !selected);
        }
    };

    return (
        <>
            <Card
                id={'senzor-' + senzor.id}
                sx={{
                    height: '100%',
                    scrollMarginTop: 80,
                    border: senzor.fault ? '2px solid' : selected ? '2px solid' : '1px solid',
                    borderColor: senzor.fault ? 'error.main' : selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.05) : undefined,
                    cursor: selectionMode ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                }}
                onClick={handleCardClick}
            >
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            {selectionMode && (
                                <Checkbox
                                    checked={selected}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        onSelect?.(senzor.id, e.target.checked);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    size="small"
                                    sx={{ mt: -0.5, ml: -1 }}
                                />
                            )}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {senzor.nazev || senzor.zigbeeId}
                                </Typography>
                                <Chip
                                    label={getSenzorTypLabel(senzor.typ)}
                                    color={getSenzorTypColor(senzor.typ)}
                                    size="small"
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Tooltip title="Upravit">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(senzor);
                                    }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {senzor.umisteni || 'Bez umístění'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            ID v síti Zigbee: {senzor.zigbeeId}
                        </Typography>
                    </Box>

                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12 }}>
                            <Tooltip title={spojeni.tooltip ?? ''}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-testid={'senzor-spojeni-' + senzor.id}>
                                    {spojeni.druh === 'online' ? (
                                        <OnlineIcon color="action" fontSize="small" />
                                    ) : (
                                        <OfflineIcon color={spojeni.barva === 'warning' ? 'warning' : 'disabled'} fontSize="small" />
                                    )}
                                    <Typography variant="body2" color={spojeni.barva === 'warning' ? 'warning.main' : 'text.secondary'}>
                                        {spojeni.text}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {senzor.baterie == null ? (
                                    <BatteryUnknownIcon color="disabled" fontSize="small" />
                                ) : senzor.baterie < 20 ? (
                                    <BatteryLowIcon color="warning" fontSize="small" />
                                ) : (
                                    <BatteryOkIcon color="success" fontSize="small" />
                                )}
                                <Typography
                                    variant="body2"
                                    color={senzor.baterie == null ? 'text.secondary' : 'text.primary'}
                                >
                                    {senzor.baterie != null ? `${senzor.baterie}%` : 'N/A'}
                                </Typography>
                            </Box>
                        </Grid>
                        {senzor.teplota != null && (
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TempIcon color="info" fontSize="small" />
                                    <Typography variant="body2">{senzor.teplota}°C</Typography>
                                </Box>
                            </Grid>
                        )}
                        {senzor.fault && (
                            <Grid size={{ xs: 6 }}>
                                <Chip
                                    icon={<WarningIcon />}
                                    label="Porucha"
                                    color="error"
                                    size="small"
                                />
                            </Grid>
                        )}
                        {isMuted && (
                            <Grid size={{ xs: 12 }}>
                                <Chip
                                    icon={<MuteIcon />}
                                    label={`Ztlumeno do ${new Date(senzor.muteDo!).toLocaleString('cs-CZ')}`}
                                    color="warning"
                                    size="small"
                                />
                            </Grid>
                        )}
                    </Grid>

                    {senzor.sirenaDisabled && (
                        <Box sx={{ mb: 1 }}>
                            <Chip
                                icon={<SirenaOffIcon />}
                                label="Siréna odpojena"
                                color="default"
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Capability-aware siren buttons */}
                        {hasWorkingSiren(capabilities) && (
                            <>
                                <Tooltip title="Spustit sirénu">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<SirenaIcon />}
                                        onClick={() => setStartSirenaConfirmOpen(true)}
                                        disabled={startSirenaMutation.isPending}
                                    >
                                        Siréna
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Zastavit sirénu">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<SirenaOffIcon />}
                                        onClick={handleStopSirena}
                                        disabled={stopSirenaMutation.isPending}
                                    >
                                        Stop
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                        {/* Legacy fallback: capabilities not loaded yet */}
                        {capabilities === undefined && LEGACY_SIREN_TYPES.has(senzor.typ) && !senzor.sirenaDisabled && (
                            <>
                                <Tooltip title="Spustit sirénu (vlastnosti zařízení se načítají)">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<SirenaIcon />}
                                        onClick={() => setStartSirenaConfirmOpen(true)}
                                        disabled={startSirenaMutation.isPending}
                                    >
                                        Siréna (?)
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Zastavit sirénu">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<SirenaOffIcon />}
                                        onClick={handleStopSirena}
                                        disabled={stopSirenaMutation.isPending}
                                    >
                                        Stop
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                        {isMuted ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<UnmuteIcon />}
                                onClick={handleUnmute}
                                disabled={unmuteMutation.isPending}
                            >
                                Zapnout
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<MuteIcon />}
                                onClick={() => setMuteDialogOpen(true)}
                            >
                                Ztlumit
                            </Button>
                        )}
                    </Box>

                    {/* Device Capabilities Panel */}
                    {capabilities && (
                        <DeviceCapabilitiesPanel capabilities={capabilities} />
                    )}
                </CardContent>
            </Card>

            {/* Mute Dialog */}
            <Dialog open={muteDialogOpen} onClose={() => setMuteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Dočasně ztlumit senzor</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Po dobu ztlumení nebudou odesílány notifikace z tohoto senzoru.
                        Události budou stále zaznamenávány.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ztlumení neukončí již vyhlášený poplach — ten deaktivujte na hlavní stránce.
                        Běžící sirénu se systém pokusí umlčet.
                    </Typography>

                    <RadioGroup
                        value={muteMode}
                        onChange={(e) => setMuteMode(e.target.value as 'minutes' | 'datetime')}
                        sx={{ mt: 2 }}
                    >
                        <FormControlLabel
                            value="minutes"
                            control={<Radio />}
                            label="Na dobu (minuty)"
                        />
                        {muteMode === 'minutes' && (
                            <TextField
                                type="number"
                                label="Doba ztlumení (minuty)"
                                value={muteMinuty}
                                onChange={(e) => setMuteMinuty(parseInt(e.target.value) || 30)}
                                fullWidth
                                sx={{ ml: 4, mt: 1, mb: 2, maxWidth: 200 }}
                                inputProps={{ min: 5, max: 10080 }}
                                size="small"
                            />
                        )}

                        <FormControlLabel
                            value="datetime"
                            control={<Radio />}
                            label="Do konkrétního data a času"
                        />
                        {muteMode === 'datetime' && (
                            <DateTimePicker
                                label="Ztlumit do"
                                value={muteDo}
                                onChange={setMuteDo}
                                minDateTime={dayjs()}
                                maxDateTime={dayjs().add(7, 'day')}
                                sx={{ ml: 4, mt: 1, mb: 1 }}
                                slotProps={{
                                    textField: { size: 'small', fullWidth: false }
                                }}
                            />
                        )}
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMuteDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleMute}
                        disabled={muteMutation.isPending}
                    >
                        {muteMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        Ztlumit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Potvrzeni spusteni sireny */}
            <Dialog open={startSirenaConfirmOpen} onClose={() => setStartSirenaConfirmOpen(false)}>
                <DialogTitle>Spustit sirénu?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu spustit sirénu na objektu <strong>{objektNazev}</strong>? Fyzicky se rozezní.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStartSirenaConfirmOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleStartSirena}
                        disabled={startSirenaMutation.isPending}
                    >
                        {startSirenaMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Spustit
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// =====================
// MAIN COMPONENT
// =====================

/** Záložky detailu objektu v pořadí zobrazení; klíč je hodnota parametru ?zalozka= v URL. */
const ZALOZKY = ['senzory', 'zarizeni', 'eps', 'historie', 'graf', 'informace'] as const;
type Zalozka = typeof ZALOZKY[number];
const jeZalozka = (v: string | null): v is Zalozka => !!v && (ZALOZKY as readonly string[]).includes(v);

/**
 * Po přepnutí záložky z odkazu (např. #zarizeni-5) posune na kartu. Obsah záložky se může ještě
 * načítat, proto se prvek chvíli hledá opakovaně.
 */
const useScrollNaKotvu = (hash: string, zalozka: string, klic: string) => {
    useEffect(() => {
        if (!hash || hash.length < 2) return undefined;
        const id = decodeURIComponent(hash.slice(1));
        let pokusy = 0;
        const casovac = window.setInterval(() => {
            const prvek = document.getElementById(id);
            if (prvek) {
                prvek.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                window.clearInterval(casovac);
            } else if (++pokusy > 30) {
                window.clearInterval(casovac);
            }
        }, 100);
        return () => window.clearInterval(casovac);
    // `klic` (location.key) se mění při každé navigaci, takže opakovaný klik na stejný odkaz posune znovu (Q5).
    }, [hash, zalozka, klic]);
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: Zalozka;
    value: Zalozka;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
        {value === index && children}
    </div>
);

const IoTObjektDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const objektId = parseInt(id || '0');

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const parametrZalozky = searchParams.get('zalozka');
    const tabValue: Zalozka = jeZalozka(parametrZalozky) ? parametrZalozky : 'senzory';
    const setTabValue = (z: Zalozka) => setSearchParams((p) => {
        const dalsi = new URLSearchParams(p);
        dalsi.set('zalozka', z);
        return dalsi;
    }, { replace: true });
    useScrollNaKotvu(location.hash, tabValue, location.key);
    const sipkyZalozek = usePreteceZalozek();

    const { data: objekt, isLoading, error } = useIotObjektQuery(objektId);
    const { data: zarizeni, isSuccess: zarizeniNactena } = useZarizeniObjektuQuery(objektId);
    const idsShellyOvladane = useMemo(
        () => (zarizeni ?? []).filter((z) => z.protokol === 'SHELLY_MQTT' && jeOvladaneZarizeni(z)).map((z) => z.id),
        [zarizeni],
    );
    const stavyZarizeni = useStavyZarizeniQuery(idsShellyOvladane);
    const { data: siaUcty } = useSiaAccountsByObjektQuery(objektId);
    const [menuObjektu, setMenuObjektu] = useState<HTMLElement | null>(null);
    const { data: capabilities } = useObjektCapabilitiesQuery(objektId);
    const updateSenzorMutation = useUpdateIotSenzorMutation();
    const muteBatchMutation = useMuteBatchSenzoryMutation();
    const muteObjektMutation = useMuteObjektMutation();
    const unmuteObjektMutation = useUnmuteObjektMutation();
    const addPrijemceMutation = useAddIotPrijemceMutation();
    const removePrijemceMutation = useRemoveIotPrijemceMutation();

    const getSenzorCaps = (senzorId: number): SenzorCapabilities | undefined => {
        if (!capabilities) return undefined; // query still loading
        return capabilities.find(c => c.senzorId === senzorId); // loaded, not found → undefined
    };

    const [prijemceDialogOpen, setPrijemceDialogOpen] = useState(false);
    const [editSenzorDialogOpen, setEditSenzorDialogOpen] = useState(false);
    const [selectedSenzor, setSelectedSenzor] = useState<IotSenzor | null>(null);
    const [senzorFormData, setSenzorFormData] = useState<UpdateIotSenzorRequest>({});
    const [mqttDialogOpen, setMqttDialogOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);

    // Selection state
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedSenzorIds, setSelectedSenzorIds] = useState<Set<number>>(new Set());

    // Batch mute dialog
    const [batchMuteDialogOpen, setBatchMuteDialogOpen] = useState(false);
    const [batchMuteMode, setBatchMuteMode] = useState<'minutes' | 'datetime'>('minutes');
    const [batchMuteMinuty, setBatchMuteMinuty] = useState(60);
    const [batchMuteDo, setBatchMuteDo] = useState<Dayjs | null>(dayjs().add(2, 'hour'));
    const [batchMuteTarget, setBatchMuteTarget] = useState<'selected' | 'object'>('selected');

    // Senzor pro historii - vyber prvni aktivni
    const [selectedHistorieSenzorId, setSelectedHistorieSenzorId] = useState<number | null>(null);
    const historieSenzorId = selectedHistorieSenzorId || objekt?.senzory?.[0]?.id;
    const { data: historie } = useIotSenzorHistorieQuery(historieSenzorId || 0);

    // Selection handlers
    const handleSenzorSelect = (senzorId: number, selected: boolean) => {
        setSelectedSenzorIds(prev => {
            const next = new Set(prev);
            if (selected) {
                next.add(senzorId);
            } else {
                next.delete(senzorId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (objekt?.senzory) {
            if (selectedSenzorIds.size === objekt.senzory.length) {
                setSelectedSenzorIds(new Set());
            } else {
                setSelectedSenzorIds(new Set(objekt.senzory.map(s => s.id)));
            }
        }
    };

    const handleCancelSelection = () => {
        setSelectionMode(false);
        setSelectedSenzorIds(new Set());
    };

    // Batch mute handler
    const handleBatchMute = () => {
        // Posíláme lokální čas bez zóny (BE ukládá do LocalDateTime) — toISOString() by
        // převedlo na UTC a posunulo čas o offset (v létě o 2 h).
        const muteData = batchMuteMode === 'datetime' && batchMuteDo
            ? { muteDo: batchMuteDo.format('YYYY-MM-DDTHH:mm:ss') }
            : { minuty: batchMuteMinuty };

        if (batchMuteTarget === 'object') {
            muteObjektMutation.mutate(
                { objektId, data: muteData },
                {
                    onSuccess: () => {
                        setBatchMuteDialogOpen(false);
                        handleCancelSelection();
                    }
                }
            );
        } else {
            muteBatchMutation.mutate(
                {
                    objektId,
                    data: {
                        senzorIds: Array.from(selectedSenzorIds),
                        mute: muteData,
                    }
                },
                {
                    onSuccess: () => {
                        setBatchMuteDialogOpen(false);
                        handleCancelSelection();
                    }
                }
            );
        }
    };

    const handleUnmuteObjekt = () => {
        unmuteObjektMutation.mutate(objektId);
    };

    const openBatchMuteDialog = (target: 'selected' | 'object') => {
        setBatchMuteTarget(target);
        setBatchMuteDialogOpen(true);
    };

    // Check if any sensors are muted
    const hasMutedSenzory = objekt?.senzory?.some(s => s.muteDo && new Date(s.muteDo) > new Date());

    const handleEditSenzor = (senzor: IotSenzor) => {
        setSelectedSenzor(senzor);
        setSenzorFormData({
            nazev: senzor.nazev || '',
            umisteni: senzor.umisteni || '',
            sirenaDisabled: senzor.sirenaDisabled || false,
        });
        setEditSenzorDialogOpen(true);
    };

    const handleSaveSenzor = () => {
        if (selectedSenzor) {
            updateSenzorMutation.mutate(
                { id: selectedSenzor.id, data: senzorFormData },
                {
                    onSuccess: () => {
                        setEditSenzorDialogOpen(false);
                        setSelectedSenzor(null);
                    },
                }
            );
        }
    };

    const handleAddPrijemce = (data: CreateIotPrijemceRequest) => {
        addPrijemceMutation.mutate(
            { objektId, data },
            {
                onSuccess: () => {
                    setPrijemceDialogOpen(false);
                },
            }
        );
    };

    const handleRemovePrijemce = (prijemceId: number) => {
        removePrijemceMutation.mutate({ objektId, prijemceId });
    };

    const historieColumns: GridColDef[] = [
        {
            field: 'cas',
            headerName: 'Čas',
            width: 180,
            valueFormatter: (value: string) =>
                value ? new Date(value).toLocaleString('cs-CZ') : '',
        },
        {
            field: 'typ',
            headerName: 'Typ',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={getUdalostTypLabel(params.value)}
                    color={getUdalostTypColor(params.value)}
                    size="small"
                />
            ),
        },
        { field: 'hodnota', headerName: 'Hodnota', width: 120 },
        {
            field: 'poplachVytvoren',
            headerName: 'Poplach',
            width: 100,
            renderCell: (params) =>
                params.value ? (
                    <Chip label="Ano" color="error" size="small" />
                ) : (
                    <Chip label="Ne" size="small" variant="outlined" />
                ),
        },
        {
            field: 'mutovano',
            headerName: 'Ztlumeno',
            width: 100,
            renderCell: (params) =>
                params.value ? (
                    <Chip label="Ano" color="warning" size="small" />
                ) : null,
        },
    ];

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !objekt) {
        return <Alert severity="error">Chyba při načítání objektu: {error?.message}</Alert>;
    }

    // Řádek stavu pod adresou (spec 4.1): jen to, co vyžaduje pozornost, jinak „Vše v pořádku“.
    // Stejný výpočet i rozsah jako čip „N nedostupných“ v záložce Zařízení: záznamy evidence s online === false (E1).
    const nedostupnaZarizeni = pocetNedostupnychEvidence(zarizeni ?? []);
    // Poctivý stav (review K6-K9, Q1): „Vše v pořádku“ jen po načtení zařízení i všech stavů bez chyby.
    const chybaStavu = idsShellyOvladane.some((zid) => stavyZarizeni[zid]?.isError);
    const nacitaSeStav = !zarizeniNactena || idsShellyOvladane.some((zid) => !stavyZarizeni[zid]?.data && !stavyZarizeni[zid]?.isError);
    const nedostupnaCidla = (objekt.senzory ?? []).filter((s) => stavSpojeniCidla(s.posledniKontakt).druh === 'nedostupne').length;
    // RPi podle BE rpiStatus (stáří posledního kontaktu), stejně jako Dashboard; surový rpiOnline zůstane true,
    // když RPi přestane hlásit bez zprávy offline (bug-inspector B1). Bez rpiStatus zbývá rpiOnline.
    // Nedostupné RPi bez ohledu na deviceId, stejné pravidlo jako Dashboard (E2): známý poslední kontakt a stav jiný než online.
    // Bez posledního kontaktu RPi „se zatím neozvalo“ (BE posílá offline_long), za nedostupné se nepočítá. To ukazujeme
    // jen u objektu s přiřazeným RPi: offline_long bez kontaktu posílá BE i objektům, které RPi vůbec nemají.
    const rpi = rpiNedostupne(objekt) ? stavSpojeniRpi(false, objekt.rpiLastOnline) : null;
    const rpiBezKontaktu = objekt.deviceId != null && rpiNeozvalo(objekt);
    const castiStavu: { text: string; barva: string; tooltip?: string }[] = [];
    // „Poplach“ v řádku stavu zatím není: BE `aktivniPoplachy` u objektu neplní (bug-inspector B2).
    if (rpi && rpi.druh === 'nedostupne') castiStavu.push({ text: rpi.text, barva: 'warning.main', tooltip: rpi.tooltip });
    if (rpiBezKontaktu) castiStavu.push({ text: 'Raspberry Pi se zatím neozvalo', barva: 'text.secondary' });
    if (nedostupnaZarizeni > 0) castiStavu.push({ text: textNedostupnaZarizeni(nedostupnaZarizeni), barva: 'warning.main' });
    if (nedostupnaCidla > 0) castiStavu.push({ text: textNedostupnaCidla(nedostupnaCidla), barva: 'warning.main' });
    if (chybaStavu) castiStavu.push({ text: 'Stav zařízení nelze zjistit', barva: 'text.secondary', tooltip: TOOLTIP_STAV_NEZNAMY });
    const radekStavu = (
        <Box component="span" data-testid="objekt-stav" sx={{ display: 'block', mt: 0.5 }}>
            {castiStavu.length === 0 ? (
                !nacitaSeStav && <Box component="span" sx={{ color: 'text.secondary' }}>Vše v pořádku</Box>
            ) : castiStavu.map((c, i) => (
                <React.Fragment key={c.text}>
                    {i > 0 && ' · '}
                    <Tooltip title={c.tooltip ?? ''}>
                        <Box component="span" tabIndex={c.tooltip ? 0 : undefined} sx={{ color: c.barva }}>{c.text}</Box>
                    </Tooltip>
                </React.Fragment>
            ))}
        </Box>
    );
    const bezObsahu = (objekt.senzory?.length ?? 0) === 0 && zarizeniNactena && (zarizeni?.length ?? 0) === 0;

    return (
        <Box>
            {/* Header */}
            <PageHeader
                title={objekt.nazev}
                subtitle={<>{formatObjektAddress(objekt) || 'Bez adresy'}{radekStavu}</>}
                icon={
                    <IconButton onClick={() => navigate('/admin/iot/objekty')}>
                        <BackIcon />
                    </IconButton>
                }
                action={
                    <>
                        {hasMutedSenzory ? (
                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={<UnmuteIcon />}
                                onClick={handleUnmuteObjekt}
                                disabled={unmuteObjektMutation.isPending}
                            >
                                Zrušit ztlumení
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                color="warning"
                                startIcon={<MuteIcon />}
                                onClick={() => openBatchMuteDialog('object')}
                            >
                                Ztlumit objekt
                            </Button>
                        )}
                        <IconButton aria-label="Další akce objektu" onClick={(e) => setMenuObjektu(e.currentTarget)}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={menuObjektu} open={!!menuObjektu} onClose={() => setMenuObjektu(null)}>
                            <MenuItem onClick={() => { setMenuObjektu(null); setWizardOpen(true); }}>
                                <WizardIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                                <ListItemText>Průvodce zprovozněním</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => { setMenuObjektu(null); setMqttDialogOpen(true); }}>
                                <SettingsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                                <ListItemText>MQTT konfigurace</ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                }
            />

            {/* Tabs */}
            <Paper sx={{ mb: 2 }} ref={sipkyZalozek.ref}>
                {/* Šipky posunu jen při skutečném přetečení (E8), viz usePreteceZalozek. */}
                <Tabs
                    value={tabValue}
                    onChange={(_, v: Zalozka) => setTabValue(v)}
                    variant="scrollable"
                    scrollButtons={sipkyZalozek.scrollButtons}
                >
                    <Tab value="senzory" label={`Senzory (${objekt.senzory?.length || 0})`} />
                    <Tab value="zarizeni" label={zarizeniNactena ? `Zařízení (${zarizeni?.length ?? 0})` : 'Zařízení'} />
                    <Tab value="eps" label="EPS/EZS" />
                    <Tab value="historie" label="Historie událostí" />
                    <Tab value="graf" label="Graf teplot" />
                    <Tab value="informace" label="Informace" />
                </Tabs>
            </Paper>

            {/* Senzory */}
            <TabPanel value={tabValue} index="senzory">
                {bezObsahu ? (
                    <Paper>
                        <EmptyState
                            icon={<WizardIcon />}
                            title="Objekt zatím nemá žádné senzory ani zařízení"
                            description="Senzory se přidají automaticky po prvním příjmu dat ze zařízení."
                            action={
                                <Button variant="contained" startIcon={<WizardIcon />} onClick={() => setWizardOpen(true)}>
                                    Spustit průvodce zprovozněním
                                </Button>
                            }
                        />
                    </Paper>
                ) : objekt.senzory?.length === 0 ? (
                    <Alert severity="info">
                        Senzory se přidají automaticky po prvním příjmu dat ze zařízení.
                    </Alert>
                ) : (
                    <>
                        {/* Selection Toolbar */}
                        <Paper sx={{ mb: 2 }}>
                            <Toolbar
                                sx={{
                                    pl: { sm: 2 },
                                    pr: { xs: 1, sm: 1 },
                                    ...(selectedSenzorIds.size > 0 && {
                                        bgcolor: (theme) =>
                                            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                                    }),
                                }}
                            >
                                {selectedSenzorIds.size > 0 ? (
                                    <>
                                        <Typography
                                            sx={{ flex: '1 1 100%' }}
                                            color="inherit"
                                            variant="subtitle1"
                                        >
                                            Vybráno: {selectedSenzorIds.size} senzorů
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                startIcon={<MuteIcon />}
                                                onClick={() => openBatchMuteDialog('selected')}
                                            >
                                                Ztlumit vybrané
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleCancelSelection}
                                            >
                                                Zrušit výběr
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{ flex: '1 1 100%' }} variant="h6">
                                            Senzory ({objekt.senzory?.length || 0})
                                        </Typography>
                                        <Tooltip title={selectionMode ? "Zrušit režim výběru" : "Režim výběru"}>
                                            <Button
                                                variant={selectionMode ? "contained" : "outlined"}
                                                size="small"
                                                startIcon={selectionMode ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                                onClick={() => {
                                                    if (selectionMode) {
                                                        handleCancelSelection();
                                                    } else {
                                                        setSelectionMode(true);
                                                    }
                                                }}
                                            >
                                                {selectionMode ? 'Zrušit výběr' : 'Vybrat'}
                                            </Button>
                                        </Tooltip>
                                        {selectionMode && objekt.senzory && objekt.senzory.length > 0 && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={handleSelectAll}
                                                sx={{ ml: 1 }}
                                            >
                                                {selectedSenzorIds.size === objekt.senzory.length
                                                    ? 'Zrušit vše'
                                                    : 'Vybrat vše'}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </Toolbar>
                        </Paper>

                        <Grid container spacing={2}>
                            {objekt.senzory?.map((senzor) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={senzor.id}>
                                    <SenzorCard
                                        senzor={senzor}
                                        objektId={objektId}
                                        objektNazev={objekt.nazev}
                                        onEdit={handleEditSenzor}
                                        selected={selectedSenzorIds.has(senzor.id)}
                                        onSelect={handleSenzorSelect}
                                        selectionMode={selectionMode}
                                        capabilities={getSenzorCaps(senzor.id)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </TabPanel>

            {/* Historie */}
            <TabPanel value={tabValue} index="historie">
                {objekt.senzory?.length ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Vyberte senzor:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {objekt.senzory.map((s) => (
                                    <Chip
                                        key={s.id}
                                        label={s.nazev || s.zigbeeId}
                                        onClick={() => setSelectedHistorieSenzorId(s.id)}
                                        color={historieSenzorId === s.id ? 'primary' : 'default'}
                                        variant={historieSenzorId === s.id ? 'filled' : 'outlined'}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Paper>
                            <DataGrid
                                rows={historie || []}
                                columns={historieColumns}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 25 } },
                                    sorting: { sortModel: [{ field: 'cas', sort: 'desc' }] },
                                }}
                                pageSizeOptions={[25, 50, 100]}
                                autoHeight
                                disableRowSelectionOnClick
                                localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                            />
                        </Paper>
                    </>
                ) : (
                    <Alert severity="info">Žádné senzory, žádná historie.</Alert>
                )}
            </TabPanel>

            {/* Graf teplot */}
            <TabPanel value={tabValue} index="graf">
                {(() => {
                    const teplotniSenzory = objekt.senzory?.filter(
                        (s) => s.typ === 'TEMPERATURE' || s.typ === 'SMOKE'
                    );
                    if (!teplotniSenzory?.length) {
                        return (
                            <Alert severity="info">
                                Žádné senzory s teplotním čidlem.
                            </Alert>
                        );
                    }
                    return (
                        <Grid container spacing={2}>
                            {teplotniSenzory.map((senzor) => (
                                <Grid size={{ xs: 12, lg: 6 }} key={senzor.id}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            {senzor.nazev || senzor.zigbeeId}
                                            {senzor.umisteni && ` (${senzor.umisteni})`}
                                        </Typography>
                                        <TeplotaChart senzorId={senzor.id} limit={288} />
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    );
                })()}
            </TabPanel>

            {/* EPS/EZS */}
            <TabPanel value={tabValue} index="eps">
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <EpsEzsSection objektId={objektId} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <RelayControlSection objektId={objektId} />
                    </Grid>
                </Grid>
            </TabPanel>

            {/* Zařízení */}
            <TabPanel value={tabValue} index="zarizeni">
                <ZarizeniSection
                    objektId={objektId}
                    objektNazev={objekt.nazev}
                    senzory={objekt.senzory ?? []}
                    siaUcty={siaUcty ?? []}
                    stavy={stavyZarizeni}
                    onPrejit={(zalozka, kotva) => navigate({ search: `?zalozka=${zalozka}`, hash: `#${kotva}` })}
                />
            </TabPanel>

            {/* Informace */}
            <TabPanel value={tabValue} index="informace">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Informace o objektu
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText primary="ID" secondary={objekt.id} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Adresa"
                                        secondary={formatObjektAddress(objekt) || 'Nevyplněno'}
                                    />
                                </ListItem>
                                {objekt.gps && (
                                    <ListItem>
                                        <ListItemText primary="GPS" secondary={objekt.gps} />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText primary="MQTT User" secondary={objekt.mqttUser || 'Nepřiřazeno'} />
                                </ListItem>
                                {objekt.deviceId != null && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Raspberry Pi, poslední kontakt"
                                            secondary={
                                                objekt.rpiLastOnline
                                                    ? new Date(objekt.rpiLastOnline).toLocaleString('cs-CZ')
                                                    : 'Zatím se neozvalo'
                                            }
                                        />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText
                                        primary="Vytvořeno"
                                        secondary={new Date(objekt.createdAt).toLocaleString('cs-CZ')}
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6">
                                    Specifičtí příjemci ({objekt.prijemci?.length || 0})
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => setPrijemceDialogOpen(true)}
                                >
                                    Přidat
                                </Button>
                            </Box>
                            <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                                <Typography variant="caption">
                                    Tito příjemci dostávají provozní upozornění (nízká baterie, výpadek čidla, teplota).
                                    Požární/CO poplach je vždy odeslán celé jednotce.
                                    {' '}Platí pouze pro tento objekt — globální příjemci z IoT Nastavení jsou automaticky zahrnuti.
                                </Typography>
                            </Alert>
                            {objekt.prijemci?.length ? (
                                <List dense>
                                    {objekt.prijemci.map((p) => (
                                        <ListItem
                                            key={p.id}
                                            sx={{
                                                bgcolor: 'grey.50',
                                                borderRadius: 1,
                                                mb: 0.5,
                                            }}
                                        >
                                            <ListItemText
                                                primary={p.uzivatelJmeno || p.email || `Uživatel #${p.uzivatelId}`}
                                                secondary={p.uzivatelId ? p.email : null}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleRemovePrijemce(p.id)}
                                                    disabled={removePrijemceMutation.isPending}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Žádní specifičtí příjemci pro tento objekt.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </TabPanel>

            {/* Edit Senzor Dialog */}
            <Dialog
                open={editSenzorDialogOpen}
                onClose={() => setEditSenzorDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Upravit senzor</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Název senzoru"
                            value={senzorFormData.nazev || ''}
                            onChange={(e) =>
                                setSenzorFormData({ ...senzorFormData, nazev: e.target.value })
                            }
                            fullWidth
                            helperText="Např. Kuchyně, Garáž, Sklad"
                        />
                        <TextField
                            label="Umístění"
                            value={senzorFormData.umisteni || ''}
                            onChange={(e) =>
                                setSenzorFormData({ ...senzorFormData, umisteni: e.target.value })
                            }
                            fullWidth
                            helperText="Např. 1. patro, u vchodu"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={senzorFormData.sirenaDisabled || false}
                                    onChange={(e) =>
                                        setSenzorFormData({ ...senzorFormData, sirenaDisabled: e.target.checked })
                                    }
                                />
                            }
                            label="Siréna mechanicky odpojena"
                        />
                        {selectedSenzor && (() => {
                            const caps = getSenzorCaps(selectedSenzor.id);
                            return caps?.model ? (
                                <Typography variant="caption" color="text.secondary">
                                    {caps.manufacturer} {caps.model}
                                </Typography>
                            ) : null;
                        })()}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditSenzorDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveSenzor}
                        disabled={updateSenzorMutation.isPending}
                    >
                        {updateSenzorMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Uložit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* MQTT Credentials Dialog */}
            <MqttCredentialsDialog
                open={mqttDialogOpen}
                onClose={() => setMqttDialogOpen(false)}
                objektId={objektId}
                objektNazev={objekt.nazev}
            />

            {/* Onboarding Wizard */}
            <OnboardingWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                objektId={objektId}
                objektNazev={objekt.nazev}
            />

            {/* Batch Mute Dialog */}
            <Dialog
                open={batchMuteDialogOpen}
                onClose={() => setBatchMuteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {batchMuteTarget === 'object'
                        ? 'Ztlumit celý objekt'
                        : `Ztlumit vybrané senzory (${selectedSenzorIds.size})`}
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {batchMuteTarget === 'object'
                            ? 'Dočasně ztlumí notifikace ze všech senzorů v tomto objektu.'
                            : 'Dočasně ztlumí notifikace z vybraných senzorů.'}
                        {' '}Události budou stále zaznamenávány.
                    </Typography>

                    <RadioGroup
                        value={batchMuteMode}
                        onChange={(e) => setBatchMuteMode(e.target.value as 'minutes' | 'datetime')}
                        sx={{ mt: 2 }}
                    >
                        <FormControlLabel
                            value="minutes"
                            control={<Radio />}
                            label="Na dobu (minuty)"
                        />
                        {batchMuteMode === 'minutes' && (
                            <Box sx={{ ml: 4, mt: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {[30, 60, 120, 240, 480].map((min) => (
                                    <Chip
                                        key={min}
                                        label={min < 60 ? `${min} min` : `${min / 60} hod`}
                                        variant={batchMuteMinuty === min ? 'filled' : 'outlined'}
                                        color={batchMuteMinuty === min ? 'primary' : 'default'}
                                        onClick={() => setBatchMuteMinuty(min)}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                                <TextField
                                    type="number"
                                    label="Jiná hodnota"
                                    value={batchMuteMinuty}
                                    onChange={(e) => setBatchMuteMinuty(parseInt(e.target.value) || 30)}
                                    size="small"
                                    sx={{ width: 120 }}
                                    inputProps={{ min: 5, max: 10080 }}
                                />
                            </Box>
                        )}

                        <FormControlLabel
                            value="datetime"
                            control={<Radio />}
                            label="Do konkrétního data a času"
                        />
                        {batchMuteMode === 'datetime' && (
                            <Box sx={{ ml: 4, mt: 1, mb: 1 }}>
                                <DateTimePicker
                                    label="Ztlumit do"
                                    value={batchMuteDo}
                                    onChange={setBatchMuteDo}
                                    minDateTime={dayjs()}
                                    maxDateTime={dayjs().add(7, 'day')}
                                    slotProps={{
                                        textField: { size: 'small' }
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label="Dnes 22:00"
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setBatchMuteDo(dayjs().hour(22).minute(0).second(0))}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                    <Chip
                                        label="Zítra 6:00"
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setBatchMuteDo(dayjs().add(1, 'day').hour(6).minute(0).second(0))}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                    <Chip
                                        label="Zítra 18:00"
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setBatchMuteDo(dayjs().add(1, 'day').hour(18).minute(0).second(0))}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBatchMuteDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleBatchMute}
                        disabled={muteBatchMutation.isPending || muteObjektMutation.isPending}
                    >
                        {(muteBatchMutation.isPending || muteObjektMutation.isPending) && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Ztlumit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Prijemce Dialog */}
            <PrijemceFormDialog
                open={prijemceDialogOpen}
                onClose={() => setPrijemceDialogOpen(false)}
                onSubmit={handleAddPrijemce}
                isLoading={addPrijemceMutation.isPending}
                title="Přidat příjemce pro tento objekt"
            />
        </Box>
    );
};

export default IoTObjektDetailPage;
